import React from 'react';
import '@brands/Dashboard/MultiStage/multiStage.scss';
import { statusLabel, statusVariant } from '@brands/Dashboard/MultiStage/multiStageUi';
import { JourneyStatus } from '@brands/services/cases/multiStage';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { useNavigate } from 'react-router-dom';

const ChevronRight = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

type Props = {
  patientId: number;
  journeyId: number;
  status: JourneyStatus;
  /** Set for a dependent's journey so the label names whose journey it is. */
  patientLabel?: string;
  /** Renders flush against the bottom edge of the appointment card above it. */
  attached?: boolean;
  style?: React.CSSProperties;
};

/**
 * The clickable "care journey" row that opens the full journey screen. Used
 * standalone in care history, and attached to an appointment card on the
 * dashboard and upcoming-appointments screens.
 */
const CareJourneyStrip = ({ patientId, journeyId, status, patientLabel, attached, style }: Props): JSX.Element => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector(selectAuth);

  const open = (): void =>
    // Keep the ids in the URL as well as in navigation state so the target page
    // survives a refresh or a shared/bookmarked link (state is lost on reload).
    navigate(`/${userInfo.role.name}/my-care-journey?patientId=${patientId}&journeyId=${journeyId}`, {
      state: { patientId, journeyId },
    });

  return (
    <div className="ms-scope" style={attached ? { width: '100%' } : undefined}>
      <div
        className={`ms-care-strip${attached ? ' attached' : ''}`}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
        style={style}
      >
        <span className="left">
          <span className="label">{patientLabel ? `${patientLabel}'s care journey` : 'My care journey'}</span>
          <span className={`ms-pill ${statusVariant(status)}`}>{statusLabel(status)}</span>
        </span>
        <span className="view">
          View
          <ChevronRight />
        </span>
      </div>
    </div>
  );
};

export default CareJourneyStrip;
